"use client"

import { useCallback, useEffect, useMemo, useState } from "react"
import { createPortal } from "react-dom"
import axios from "axios"
import { Box, Select, Text, Alert, AlertIcon, AlertDescription, useToast } from "@chakra-ui/react"
import { FaUserTie, FaStore, FaFileExcel } from "react-icons/fa"

import PageToolbar from "../../components/shared/PageToolbar"
import Section, { Panel } from "../../components/shared/Section"
import ExportMenu from "../../components/shared/ExportMenu"
import { useSupplierRetailer } from "../../wrappers/SupplierRetailerContext"
import { neutral, brand, type } from "../../theme/tokens"

import RepKpis from "./components/RepKpis"
import RepSummary from "./components/RepSummary"
import RepTracker from "./components/RepTracker"
import RepChart from "./components/RepChart"

const API = process.env.REACT_APP_API_URL
const ALL = "ALL"
const REP_KEY = "spearbi_rep_selected"

/**
 * Rep page (docs/michael/blue-mango-rep-report/TASK-rep-report.md, 2026-09-21).
 *
 * Organisation-scoped: every number combines all of the organisation's suppliers (the
 * "Combined" switcher in the rail) and the Dischem channel only — so no supplier or
 * retailer rides in the requests, only the page's own filters (rep, supplier) and, for a
 * master admin, the org toggle (org_code). The backend refuses an organisation without
 * rep→store assignments (403 REP_NOT_SET_UP), which this page renders as a notice.
 *
 * Numbers are sales AT COST from dis_sales.cost — the workbook's data!H — never retail.
 */
// Combined page (RepHub, 2026-10-12): `routing` = the Rep Routing summary, joined into the All reps table per rep;
// `chartTarget` = a node at the foot of that page — the Sales at cost by rep chart renders there, not beside the table.
const Rep = ({ routing = null, onRoute, chartTarget = null }) => {
  const { selectedOrg, isMaster } = useSupplierRetailer()
  const toast = useToast()

  const [options, setOptions] = useState(null)
  const [optionsError, setOptionsError] = useState(null) // { code, message }
  const [supplier, setSupplier] = useState(ALL)
  const [rep, setRep] = useState(() => { try { return localStorage.getItem(REP_KEY) || "" } catch (e) { return "" } })
  // The chart always opens on the current month (Helena 2026-10-13) — a switch to the previous month is no longer remembered.
  const [pair, setPair] = useState("cur")
  const [summary, setSummary] = useState(null)
  const [summaryLoading, setSummaryLoading] = useState(true)
  const [tracker, setTracker] = useState(null)
  const [trackerLoading, setTrackerLoading] = useState(true)
  const [exporting, setExporting] = useState(false)

  const orgArg = isMaster ? { org_code: selectedOrg } : {}
  const body = useCallback((extra = {}) => ({ userid: localStorage.getItem("userid"), ...orgArg, ...extra }), [selectedOrg, isMaster]) // eslint-disable-line react-hooks/exhaustive-deps

  // Options: reps, suppliers, period. Re-fetched when a master changes organisation.
  useEffect(() => {
    const controller = new AbortController()
    setOptions(null)
    setOptionsError(null)
    setSupplier(ALL)
    axios
      .post(`${API}/rep_report/options`, body(), { signal: controller.signal })
      .then((res) => {
        const data = res.data?.data
        setOptions(data)
        // Keep the remembered rep when it still exists, else the combined view.
        const names = (data?.reps || []).map((r) => r.name)
        setRep((prev) => (prev === ALL || prev === "Unassigned" || names.includes(prev) ? prev : ALL))
      })
      .catch((err) => {
        if (axios.isCancel(err)) return
        const d = err.response?.data
        setOptionsError({ code: d?.code || (err.response?.status === 403 ? "FORBIDDEN" : "ERROR"), message: d?.message || "The Rep page could not be loaded." })
      })
    return () => controller.abort()
  }, [selectedOrg, isMaster]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { try { if (rep) localStorage.setItem(REP_KEY, rep) } catch (e) { /* private mode */ } }, [rep])

  // Summary (every rep) follows the supplier filter.
  useEffect(() => {
    if (!options) return undefined
    const controller = new AbortController()
    setSummaryLoading(true)
    axios
      .post(`${API}/rep_report/summary`, body({ supplier }), { signal: controller.signal })
      .then((res) => setSummary(res.data?.data || null))
      .catch((err) => { if (!axios.isCancel(err)) toast({ title: "Summary failed", description: err.response?.data?.message || err.message, status: "error", duration: 5000, isClosable: true }) })
      .finally(() => { if (!controller.signal.aborted) setSummaryLoading(false) })
    return () => controller.abort()
  }, [options, supplier]) // eslint-disable-line react-hooks/exhaustive-deps

  // Tracker (one rep's stores) follows the rep and the supplier filter.
  useEffect(() => {
    if (!options || !rep) return undefined
    const controller = new AbortController()
    setTrackerLoading(true)
    axios
      .post(`${API}/rep_report/tracker`, body({ rep, supplier }), { signal: controller.signal })
      .then((res) => setTracker(res.data?.data || null))
      .catch((err) => { if (!axios.isCancel(err)) toast({ title: "Tracker failed", description: err.response?.data?.message || err.message, status: "error", duration: 5000, isClosable: true }) })
      .finally(() => { if (!controller.signal.aborted) setTrackerLoading(false) })
    return () => controller.abort()
  }, [options, rep, supplier]) // eslint-disable-line react-hooks/exhaustive-deps

  // Excel — same endpoint for one rep or every rep; window.fetch carries the bearer token
  // (tools/demoInterceptor.js), exactly as the other report downloads do.
  const download = useCallback(async (which) => {
    const repArg = which === ALL ? ALL : rep
    const safe = (s) => String(s).replace(/[^A-Za-z0-9 _.-]+/g, "_").trim()
    const fileName = `Rep Sales_${safe(repArg === ALL ? "All reps" : repArg)}_${safe(supplier)}.xlsx`
    try {
      setExporting(true)
      // (rep = ALL → Summary + All stores + one sheet per rep)
      const res = await fetch(`${API}/rep_report/tracker-xlsx`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body({ rep: repArg, supplier })),
      })
      const ct = res.headers.get("content-type") || ""
      if (!res.ok || !ct.includes("spreadsheetml")) {
        let message = `HTTP ${res.status}`
        try { message = (await res.json()).message || message } catch (e) { /* not json */ }
        throw new Error(message)
      }
      const blob = await res.blob()
      const link = document.createElement("a")
      link.href = window.URL.createObjectURL(blob)
      link.download = fileName
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(link.href)
      toast({ title: "Downloaded", description: fileName, status: "success", duration: 4000, isClosable: true })
    } catch (err) {
      toast({ title: "Download failed", description: err.message, status: "error", duration: 6000, isClosable: true })
    } finally {
      setExporting(false)
    }
  }, [rep, supplier, body, toast])

  // KPI strip: the selected rep's totals (the tracker's total row), or every rep's when none.
  const kpiMeasures = useMemo(() => {
    if (tracker && tracker.rep === rep && tracker.total) return tracker.total
    return summary?.total || null
  }, [tracker, summary, rep])

  const period = options?.period || summary?.period || null
  const reps = options?.reps || []
  const suppliers = options?.suppliers || []
  const costMissing = summary?.costMissing?.length ? summary.costMissing : options?.costMissing || []

  if (optionsError) {
    return (
      <Panel>
        <Text fontSize={type.size.body} fontWeight={type.weight.semibold} color={neutral[800]} mb={1}>
          {optionsError.code === "REP_NOT_SET_UP" ? "Rep page not set up" : "Rep page unavailable"}
        </Text>
        <Text fontSize={type.size.small} color={neutral[500]}>{optionsError.message}</Text>
      </Panel>
    )
  }

  const selectStyle = { size: "sm", bg: neutral[0], borderColor: neutral[300], fontSize: type.size.small, w: "auto", minW: "190px" }

  return (
    <Box>
      <PageToolbar
        description={options?.org?.org_name ? `${options.org.org_name} · Dischem · sales at cost per rep` : "Dischem · sales at cost per rep"}
        filters={
          <>
            <Select {...selectStyle} value={rep} onChange={(e) => setRep(e.target.value)} isDisabled={!options} aria-label="Rep">
              <option value={ALL}>Combined · all reps</option>
              {reps.map((r) => (
                <option key={r.name} value={r.name}>{r.name} ({r.stores})</option>
              ))}
              {summary?.unassigned && <option value="Unassigned">Unassigned stores ({summary.unassigned.stores})</option>}
            </Select>
            <Select {...selectStyle} value={supplier} onChange={(e) => setSupplier(e.target.value)} isDisabled={!options} aria-label="Supplier">
              <option value={ALL}>Combined · all suppliers</option>
              {suppliers.map((s) => (
                <option key={s.value} value={s.value}>{s.value}</option>
              ))}
            </Select>
          </>
        }
        actions={
          <ExportMenu
            isLoading={exporting}
            width="300px"
            items={
              rep === ALL
                ? [{ label: "Combined · summary, all stores + one sheet per rep", onClick: () => download(ALL), icon: FaFileExcel }]
                : [
                    { label: `Rep Sales · ${rep || "rep"}`, onClick: () => download(rep), icon: FaFileExcel },
                    { label: "All reps · summary, all stores + one sheet per rep", onClick: () => download(ALL), icon: FaFileExcel },
                  ]
            }
          />
        }
      />

      {costMissing.length > 0 && (
        <Alert status="warning" variant="left-accent" borderRadius="md" mb={4} fontSize={type.size.small}>
          <AlertIcon />
          <AlertDescription>
            Sales at cost has not been captured for {costMissing.map((m) => m.label).join(", ")} — those cells show blank until that month's Dischem file is imported again.
          </AlertDescription>
        </Alert>
      )}

      <RepKpis measures={kpiMeasures} period={period} rep={tracker && tracker.rep === rep && rep !== ALL ? rep : null} isLoading={!options || (summaryLoading && trackerLoading)} />

      <Section
        title="All reps"
        description="Every rep's stores combined · click a rep to open their tracker"
        icon={FaUserTie}
        accent={brand[500]}
        meta={period ? <Text fontSize={type.size.caption} color={neutral[400]}>Data to {period.latestDay}</Text> : null}
      >
        {/* Side by side only on very wide screens — the eight-column table needs the width;
            below that the chart sits under it. */}
        {chartTarget ? (
          <RepSummary data={summary} isLoading={summaryLoading && !summary} selectedRep={rep} onSelectRep={setRep} routing={routing} onRoute={onRoute} />
        ) : (
          <Box display="grid" gridTemplateColumns={{ base: "1fr", "2xl": "minmax(0, 3fr) minmax(0, 2fr)" }} gap={4} alignItems="start">
            <RepSummary data={summary} isLoading={summaryLoading && !summary} selectedRep={rep} onSelectRep={setRep} />
            <RepChart summary={summary} pair={pair} onPairChange={setPair} selectedRep={rep} />
          </Box>
        )}
      </Section>

      <Section
        title="Rep Sales"
        description={rep === ALL ? "Combined · every store of every rep, blank where nothing sold" : rep ? `${rep} · every assigned store, blank where nothing sold` : "Pick a rep"}
        icon={FaStore}
        accent="#8A63D2"
      >
        <RepTracker data={tracker} isLoading={trackerLoading && !tracker} />
      </Section>
      {chartTarget && createPortal(<RepChart summary={summary} pair={pair} onPairChange={setPair} selectedRep={rep} />, chartTarget)}
    </Box>
  )
}

export default Rep
